import { useEffect, useState } from "react";
import {
  App,
  Button,
  Flex,
  notification,
  Select,
  SelectProps,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { useTranslation } from "react-i18next";
import { EditOutlined } from "@ant-design/icons";

import api from "@/apis/customer";
import priceApis from "@/apis/price";
import { Customer, PriceAdd, PriceHis } from "@/types/sales";
import PriceForm from "@/pages/Sales/components/SPriceForm";
import useDataUpdate from "@/hooks/useDataUpdate";
import useDataList from "@/hooks/useDataList";
import { CommonListParams } from "@/types/common";
import { dateFormat, priceFormat } from "@/utils/format";
import dayjs from "dayjs";
import SText from "@/styles/SText";
import { DefaultOptionType } from "antd/lib/select";

const { Text } = Typography;

type SelectValue = PriceHis | null;

interface SCurrencySelectProps extends SelectProps {
  customerId: string;
  value?: SelectValue;
  onChange?: (value: PriceHis) => void;
  defaultList?: Array<PriceHis>;
}

const SelectPrice = ({
  value,
  onChange,
  customerId,
  defaultList = [],
  ...props
}: SCurrencySelectProps) => {
  const { t } = useTranslation();
  const { update: updatePrice, loading: updateLoading } = useDataUpdate<[PriceAdd, string]>({ api: priceApis.update });
  const [list, , listLoading, { fetchList, updateItem }] = useDataList<PriceHis, [string, CommonListParams?]>({ fetchApi: api.latestPrices });

  const [detail, setDetail] = useState<PriceHis | null>(null);
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    if (customerId && open && list.length === 0) {
      fetchList(customerId);
    }
  }, [customerId, open, list.length, fetchList]);

  const filter = (input: string, option: DefaultOptionType | undefined) => {
    return (option?.data?.product?.name ?? "").toLowerCase().includes(input.toLowerCase()) || (option?.data?.product?.code ?? "").toLowerCase().includes(input.toLowerCase())
  }

  return (
    <>
      <Select open={open}
        value={value?.id ? value?.id : undefined}
        filterOption={filter}
        onDropdownVisibleChange={(val) => {
          setOpen(val)
          if (val) {
            if (!customerId) {
              notification.error({
                placement: 'top',
                message: t('sale.plCustomer'),
              });
            }
          }
        }}
        onChange={(val, info) => {
          onChange && onChange((info as { data: PriceHis }).data);
        }}
        style={{ width: "100%" }}
        showSearch
        // options={list}
        options={[...defaultList, ...list.filter(item => !defaultList.find(dItem => dItem.id === item.id))].map((item) => ({
          key: item.id,
          value: item.id,
          data: item,
          label: (
            <Flex justify="space-between">
              <SText text={item.product?.name + `(${item.product?.code})`} />
              <Tag color="blue">{item.price}</Tag>
            </Flex>
          ),
        }))}
        placeholder={t("sale.plProductPrice")}
        optionRender={(option) => {
          const { data } = option

          return (
            <Tooltip title={
              <div>
                <div>{t('product.code')}: {data.data?.product?.code ?? ''}</div>
                <div>{t('product.name')}: {data.data?.product?.name ?? ''}</div>
                <div>{t('product.brand1')}: {data.data?.product?.model?.toLocaleUpperCase() ?? ''}</div>
                <div>{t('product.brand2')}: {data.data?.product?.t1?.toLocaleUpperCase() ?? ''}</div>
                <div>{t('sale.estimateDays')}: {data.data?.estimateDays ?? ''}</div>
                <div>{t('sale.expiredAt')}: {data.data?.expiredAt ? dateFormat(dayjs(data.data?.expiredAt)) : ''}</div>
                <div>{t('sale.customerModel')}: {data.data?.customerModel ?? ''}</div>
                <div>{t('sale.mfgr')}: {data.data?.mfgr ?? ''}</div>
              </div>
            }>
              <Flex gap={10} style={{ width: '100%' }} align='center' justify='space-between'>
                <Text style={{ width: '20%' }} ellipsis>{data.data?.product?.name ?? ''}</Text>
                <Text style={{ width: '10%' }}>{priceFormat(data.data?.price as string ?? '')}</Text>
                <Text style={{ width: '20%' }} ellipsis>{data.data?.product?.code ?? ''}</Text>
                <Text style={{ width: '30%' }} ellipsis>{data.data?.product?.model?.toLocaleUpperCase() ?? ''}</Text>
                <Button type="text" icon={<EditOutlined />} onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setDetail(data.data as PriceHis);
                }} />
              </Flex>
            </Tooltip>
          );
        }}
        dropdownRender={(ele) => (
          <>
            {ele}
            {listLoading && (
              <Flex
                style={{ width: "100%", padding: 10 }}
                justify="center"
                align="center"
              >
                <Spin />
              </Flex>
            )}
          </>
        )}
        {...props}
      />
      {detail && <PriceForm
        key={'addPrice'}
        open={!!detail}
        customer={detail.customer as Customer}
        confirmLoading={updateLoading}
        handleCancel={() => setDetail(null)}
        handleOk={(data) => {
          return new Promise((resolve, reject) => {
            if (detail) {
              updatePrice({ ...data, customerId: detail.customerId }, detail.priceId)
                .then(() => {
                  updateItem({ ...detail, ...data });
                  setDetail(null);
                  resolve();
                })
                .catch((err) => {
                  const map: {
                    [key in number]: string;
                  } = {
                    10010: t('common.networkError'),
                  };
                  notification.error({
                    placement: 'top',
                    message: t('common.addError'),
                    description: map[err.code] ?? t('common.serverError'),
                  });
                  reject();
                });
            }
          });
        }}
        leftTitle={t('common.edit')}
        data={{
          customerId: detail.customerId,
          productId: detail.productId,
          price: detail.price,
          desc: '',
          id: detail.priceId,
          logBookId: '',
          product: detail.product,
          customer: detail.customer,
          estimateDays: detail.estimateDays,
          expiredAt: detail.expiredAt,
          mfgr: detail.mfgr,
          customerModel: detail.customerModel
        }}
      />}
    </>
  );
};

export default SelectPrice;
